/**
 * The client's view of `API_CONTRACT.md` §5 and §6.
 *
 * Two rules from the contract are enforced here rather than left to each screen:
 *
 *  1. **Branch on `code`, never on `message`** (§5). `message` is prose that may be
 *     reworded; `code` is the contract. Every helper below reads `code`.
 *  2. **`An toàn hiển thị` decides whether the server's sentence may be shown**
 *     (§5.1). A code marked "Không" would help an attacker probe system state, so
 *     the UI renders a generic sentence and the correlation id instead. The set
 *     below is the "Có" column, transcribed; anything not in it is treated as
 *     unsafe, which is the fail-closed direction for a list that could go stale.
 *     `error-safety.test.ts` compares the list against the document as a set, so it
 *     cannot go stale silently.
 */

import type { WorkforceErrorBody } from './types';

/**
 * Codes whose `message` may be rendered verbatim — **every** row of
 * `API_CONTRACT.md` section 6 whose `An toàn hiển thị` column says `Có`, in document
 * order, with the section each came from named.
 *
 * ── This list used to be a subset, and that was a defect ────────────────────
 *
 * It previously carried 30 of the 57, on the stated reasoning that groups E, F and G
 * are agent transport and "unreachable from a browser". The reasoning was sound and
 * the execution was not: **section 6.9, business state, was omitted entirely**, and
 * every code in it is one a dashboard write produces. So `WORKFORCE_TIMER_ALREADY_ACTIVE`
 * on `/time-tracking`, `WORKFORCE_REASON_WINDOW_EXPIRED` on a correction — which
 * `useApiMutation`'s own docblock calls "the **only** published statement of
 * `wp_policies.correction_max_days_back` a browser can obtain" — and
 * `WORKFORCE_INVALID_STATE_TRANSITION` on a device revoke all rendered as
 * "Đã xảy ra lỗi. Vui lòng thử lại", discarding the one sentence that told the
 * person what to do. Found by WP-K5 (Task 45) asserting the 409 on a second revoke.
 *
 * The "restricted to what a session can receive" clause is gone rather than
 * corrected. It bought nothing — a code a browser never receives simply never renders
 * — and it cost twenty-seven wrong sentences, because it required a per-code judgement
 * that nothing checked. The document is the authority on which messages are safe to
 * show; this is a transcription of that column and nothing else, and
 * `web/src/api/error-safety.test.ts` compares the two as **sets** so the next row
 * added to section 6 is covered the moment it exists.
 *
 * Anything not listed is still treated as unsafe, which is the fail-closed direction.
 */
const USER_SAFE_CODES: ReadonlySet<string> = new Set([
  // API_CONTRACT.md section 6.1
  'WORKFORCE_UNAUTHENTICATED',
  'WORKFORCE_SESSION_EXPIRED',
  'WORKFORCE_LOGIN_FAILED',
  'WORKFORCE_CSRF_FAILED',
  'WORKFORCE_ACCOUNT_INACTIVE',
  'WORKFORCE_ACCOUNT_NO_COMPANY',
  // API_CONTRACT.md section 6.2
  'WORKFORCE_ENTITLEMENT_MISSING',
  'WORKFORCE_COMPANY_DISABLED',
  'WORKFORCE_PERMISSION_DENIED',
  'WORKFORCE_SCOPE_DENIED',
  'WORKFORCE_SENSITIVE_DATA_DENIED',
  'WORKFORCE_ACCESS_REASON_REQUIRED',
  'WORKFORCE_COMPANY_SELECTION_REQUIRED',
  'WORKFORCE_COMPANY_NOT_A_MEMBER',
  // WP-BR-07. A prohibited MCP tool name — safe to show, it names no data.
  'WORKFORCE_TOOL_PROHIBITED',
  // API_CONTRACT.md section 6.3
  'WORKFORCE_SERVICE_DISABLED',
  'WORKFORCE_NOT_IMPLEMENTED',
  'WORKFORCE_FEATURE_UNAVAILABLE',
  'WORKFORCE_AI_UNAVAILABLE',
  'WORKFORCE_STORAGE_UNAVAILABLE',
  'WORKFORCE_SERVICE_UNAVAILABLE',
  // API_CONTRACT.md section 6.4
  'WORKFORCE_VALIDATION_FAILED',
  'WORKFORCE_PAYLOAD_TOO_LARGE',
  'WORKFORCE_RATE_LIMITED',
  'WORKFORCE_QUOTA_EXCEEDED',
  'WORKFORCE_NOT_FOUND',
  'WORKFORCE_CONFLICT',
  'WORKFORCE_VERSION_CONFLICT',
  'WORKFORCE_IDEMPOTENCY_KEY_REUSED',
  'WORKFORCE_INTERNAL_ERROR',
  'WORKFORCE_ACHAU_ID_INVALID',
  // API_CONTRACT.md section 6.5
  'WORKFORCE_DEVICE_NOT_FOUND',
  // API_CONTRACT.md section 6.7
  'WORKFORCE_REGISTRATION_TOKEN_INVALID',
  'WORKFORCE_REGISTRATION_TOKEN_EXPIRED',
  'WORKFORCE_REGISTRATION_TOKEN_EXHAUSTED',
  'WORKFORCE_DEVICE_ALREADY_REGISTERED',
  // API_CONTRACT.md section 6.8
  'WORKFORCE_ACHAU_AUTH_PATH_NOT_FOUND',
  'WORKFORCE_UPSTREAM_FAILED',
  'WORKFORCE_UPSTREAM_TIMEOUT',
  'WORKFORCE_UPSTREAM_RATE_LIMITED',
  'WORKFORCE_CIRCUIT_OPEN',
  'WORKFORCE_TASK_NOT_FOUND',
  'WORKFORCE_TASK_CLOSED',
  'WORKFORCE_AI_OUTPUT_INVALID',
  // API_CONTRACT.md section 6.9
  'WORKFORCE_INVALID_STATE_TRANSITION',
  'WORKFORCE_TIMER_ALREADY_ACTIVE',
  'WORKFORCE_TIMER_NOT_ACTIVE',
  'WORKFORCE_SHIFT_ASSIGNMENT_OVERLAP',
  'WORKFORCE_FORMULA_IMMUTABLE',
  'WORKFORCE_FORMULA_WEIGHTS_INVALID',
  'WORKFORCE_APPROVAL_NOT_ALLOWED',
  'WORKFORCE_REPORT_NOT_READY',
  'WORKFORCE_REPORT_FAILED',
  'WORKFORCE_REPORT_EXPIRED',
  'WORKFORCE_RETENTION_LOCKED',
  'WORKFORCE_WEBHOOK_URL_REJECTED',
  'WORKFORCE_EMPLOYEE_NOT_FOUND',
  'WORKFORCE_REASON_WINDOW_EXPIRED',
  'WORKFORCE_GEO_OUTSIDE_GEOFENCE',
  'WORKFORCE_SHIFT_OVERLAP',
  'WORKFORCE_DEDUCTION_CAP_EXCEEDED',
  'WORKFORCE_PAYROLL_COUNTRY_UNSUPPORTED',
]);

/**
 * The transport had no answer at all: DNS, TLS, an aborted fetch, a body that was
 * not the contract's JSON. Not a `WORKFORCE_*` code, and deliberately spelled so it
 * cannot be mistaken for one in a log or a bug report.
 */
export const CLIENT_TRANSPORT_FAILED = 'CLIENT_TRANSPORT_FAILED';

/** The four refusals the shared `PermissionDenied` state renders (§11 chain steps 3–7). */
export const PERMISSION_DENIED_CODES = [
  'WORKFORCE_PERMISSION_DENIED',
  'WORKFORCE_SCOPE_DENIED',
  'WORKFORCE_SENSITIVE_DATA_DENIED',
  'WORKFORCE_ACCESS_REASON_REQUIRED',
] as const;

export type PermissionDeniedCode = (typeof PERMISSION_DENIED_CODES)[number];

const PERMISSION_DENIED_SET: ReadonlySet<string> = new Set(PERMISSION_DENIED_CODES);

export class WorkforceApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Readonly<Record<string, unknown>>;
  readonly correlationId: string | null;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    details?: Readonly<Record<string, unknown>>;
    correlationId?: string | null;
  }) {
    super(init.message);
    this.name = 'WorkforceApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details ?? {};
    this.correlationId = init.correlationId ?? null;
  }

  /** §5.1: may this error's own sentence be shown to the person reading the screen? */
  get userSafe(): boolean {
    return USER_SAFE_CODES.has(this.code);
  }

  /** §6.4: `details.retry_after_seconds` on a 429, when the server sent one. */
  get retryAfterSeconds(): number | null {
    const value = this.details.retry_after_seconds;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
}

export function isWorkforceApiError(value: unknown): value is WorkforceApiError {
  return value instanceof WorkforceApiError;
}

/** 401 — the session is gone or expired. The shell sends the person back to `/login`. */
export function isUnauthenticated(error: WorkforceApiError): boolean {
  return error.code === 'WORKFORCE_UNAUTHENTICATED' || error.code === 'WORKFORCE_SESSION_EXPIRED';
}

export function permissionDeniedCode(error: WorkforceApiError): PermissionDeniedCode | null {
  return PERMISSION_DENIED_SET.has(error.code) ? (error.code as PermissionDeniedCode) : null;
}

/**
 * Worth offering a `RetryAction` for.
 *
 * A refusal is not retryable — pressing a button will not grant a permission, and a
 * retry control under a permission refusal invites a person to hammer an endpoint
 * that is answering correctly. Transport failures, 5xx and 429 are.
 */
export function isRetryable(error: WorkforceApiError): boolean {
  if (error.code === CLIENT_TRANSPORT_FAILED) return true;
  if (error.status === 429) return true;
  return error.status >= 500;
}

/**
 * Parse the contract's error envelope out of a response body.
 *
 * Anything that is not the four-field object is reported as a transport failure
 * carrying the HTTP status, rather than as a fabricated `WORKFORCE_*` code: a proxy
 * error page and a service refusal are different events and must not be merged.
 */
export function toApiError(
  status: number,
  body: unknown,
  correlationId: string | null,
): WorkforceApiError {
  // `Partial<WorkforceErrorBody>` made only the *top* level optional, which told
  // TypeScript every field of `error` was present — so `no-unnecessary-condition`
  // read the `?? {}` below as dead code when it is the only thing between a
  // truncated envelope and a `details` of `undefined`. The nested `Partial` says
  // what is actually known about a body that came off the wire: nothing.
  const envelope = body as { readonly error?: Partial<WorkforceErrorBody['error']> } | null;
  const error = envelope?.error;
  if (
    error !== undefined &&
    typeof error.code === 'string' &&
    typeof error.message === 'string' &&
    /^WORKFORCE_[A-Z0-9_]+$/.test(error.code)
  ) {
    return new WorkforceApiError({
      code: error.code,
      message: error.message,
      status,
      details: error.details ?? {},
      correlationId:
        typeof error.correlation_id === 'string' ? error.correlation_id : correlationId,
    });
  }
  return new WorkforceApiError({
    code: CLIENT_TRANSPORT_FAILED,
    message: `HTTP ${String(status)} without a Workforce error body`,
    status,
    correlationId,
  });
}
